// The UI wiring shared by every x01 variant (501, 301, ...): a turn-based
// scoreboard + always-visible numpad, not a cell-entry or closing-game board,
// so unlike most other games here it doesn't reuse components/rounds-grid.js
// - it renders its own scoreboard + numpad directly into the mounted
// container. The digit-entry mechanics (and the .keypad-display/.keypad-grid/
// .keypad-btn classes for a consistent look) are still borrowed from
// components/keypad.js and styles.css, since a turn score is entered exactly
// like any other numeric keypad value - this game just has nowhere to put a
// modal (there's no "cell" to open one from), so the pad is always visible
// instead of appearing in the shared keypad modal.
//
// All state lives in a closure per createX01GameController() call (not
// module-level) so 501 and 301 - each its own module importing this factory -
// never share state, the same pattern games/shared/closing-game-controller.js
// uses for Cricket/Tactics/Random Cricket/Cutthroat.
import { digitInput, digitClear, digitBackspace, resetKeypadEntryState } from '../../components/keypad.js';
import { configRowDefault } from './config-rows.js';
import { showPlainModal, hidePlainModal, isModalOpen } from '../../core/modal.js';
import { simulateAiTurn } from './x01-ai.js';
import { DIFFICULTY_TIERS } from '../../core/ai-difficulty.js';
import { showCpuThrowOverlay, waitBeforeFirstDart, revealCpuDart, drawCpuDart, showAimMarker, setCpuThrowOutcome, finishCpuThrowOverlay, waitBetweenCpuTurns } from '../../components/cpu-dartboard-overlay.js';

function randomIntFromInterval(min, max) { // min and max included
    return Math.floor(Math.random() * (max - min + 1) + min);
}

// One-tap shortcuts for the most common 3-dart totals, always visible
// alongside the numpad - a full turn is very often one of these.
const QUICK_SCORES = [180, 140, 100, 85, 60, 45, 41, 26];
// How long the "leg won" modal stays up before closing itself - also the
// length of the "Game on!" button's fill animation (see styles.css).
const LEG_WON_AUTOCLOSE_MS = 8000;

export function createX01GameController(x01) {
    let currentPlayers = [];
    let remaining = {};
    // player -> {score, leg, darts}[] of every turn's recorded score (this leg
    // + all previous) plus which leg it belongs to, so per-leg averages can be
    // derived (leg is restored right along with everything else in a snapshot).
    let turnHistory = {};
    let currentLegNumber = 0; // bumped once per completed leg - never reset mid-match, only at mount()
    let dartsThrown = {}; // player -> total darts thrown so far, for a real 3-dart average (checkouts often take fewer than 3)
    // player -> {leg, score, darts}[], one entry per leg *won* by that player -
    // score is the checkout (the remaining they finished from), darts is the
    // total darts that player threw across the whole leg (not just the
    // finishing turn), for the stats modal's Highest Finish/Best Leg.
    let finishes = {};
    let sets = {};
    let legs = {};
    let setsToWin = 2;
    let legsToWin = 2;
    let bestOfSets = configRowDefault(x01, 'bestOfSets');
    let bestOfLegs = configRowDefault(x01, 'bestOfLegs');
    let startingScore = configRowDefault(x01, 'startingScore');
    let activeIndex = 0;
    let history = []; // stack of full-state snapshots, for undo
    let matchComplete = false;
    let onChangeCallback = null;
    let toastTimer = null;
    // Set only while the "how many darts?" modal is up after a checkout - the
    // turn that finished the leg isn't committed (turnHistory/dartsThrown/
    // legs/sets/turn order) until the player answers it.
    let pendingFinish = null;
    // The most recent toast, carried in getState() so every synced client
    // (an observer especially - it never runs confirmTurn/showToast itself)
    // sees the same "BUST", "X wins the leg!", etc. feedback, not just the
    // client that actually took the turn. `seq` is how setState() tells a
    // genuinely new toast apart from the same one arriving again (a
    // duplicate envelope, or this same client's own change echoing back).
    let toastSeq = 0;
    let lastToast = null; // { msg, kind, seq } | null
    let lastAppliedToastSeq = 0; // highest seq this client has already shown (via showToast() itself or a setState() replay)
    // The most recent leg/set win, carried in getState() the same way
    // lastToast is so every synced client (observers included) opens the same
    // "leg won" modal. `seq` plays the same role as toastSeq's.
    let legWinSeq = 0;
    let lastLegWin = null; // { seq, kind: 'leg' | 'set', player, average, next } | null
    let lastAppliedLegWinSeq = 0;
    // The live progress of a Computer turn currently being simulated on this
    // client, carried in getState() the same way lastToast/lastLegWin are -
    // but unlike those, this is pushed *repeatedly* while the turn is still
    // in flight (see animateCpuTurn's own notifyChange() calls), not once at
    // the end, so every other client (an observer, or a human player's own
    // device in the same session - see setState()) opens the board the
    // instant the turn starts and reveals each dart the moment it actually
    // lands, instead of only ever hearing about the whole turn after the
    // driving client already finished showing it. `plan` is
    // simulateAiTurn()'s full return value (every dart's aim/actual
    // landing/outcome) computed upfront, `revealed` is how many of its
    // darts have landed so far, and `done` marks the outcome line as
    // showing (the mirrored overlay itself closes once this goes back to
    // null - see commitAiTurn/animateCpuTurn). `seq` tells one turn's
    // progression apart from the next play through the same player.
    let cpuTurnSeq = 0;
    let cpuTurnLive = null; // { seq, player, plan, revealed, done } | null
    // A non-driving client's own view of cpuTurnLive - how much of the
    // *current* seq it has already drawn, so a snapshot re-arriving with no
    // new progress (a duplicate envelope, a reconnect) never redraws darts
    // already shown. Local UI state only, never part of getState()/
    // setState() itself.
    let observerCpuSeq = 0;
    let observerCpuRevealed = 0;
    let observerMode = false;
    let simulating = false;
    let legWonTimer = null;
    // player -> throwProfile (see core/ai-difficulty.js) for whichever
    // players are Computer-controlled - empty for a match with no CPU
    // players, or on any client that isn't the one driving them (see
    // mount()'s own comment on cpuPlayers). Never part of getState()/
    // setState() - it's local config, not shared match state.
    let cpuPlayers = {};
    let cpuTurnInProgress = false; // re-entrancy guard - maybeRunCpuTurn() can otherwise be kicked off more than once for the same turn
    // Set true by stopCpuTurn() (app.js calls it when leaving the game
    // screen - back button, Home, Restart) - a CPU's turn otherwise keeps
    // simulating and committing in the background even after its own
    // overlay is off screen, since nothing about navigating away actually
    // stops the async chain in maybeRunCpuTurn()/animateCpuTurn() below.
    // Checked at every point that chain resumes after an await; reset back
    // to false in mount() for a genuinely new game.
    let cpuTurnStopped = false;
    // The overlay currently on screen for an in-progress CPU turn, if any -
    // so stopCpuTurn() can remove it immediately, regardless of where in
    // its own await chain that turn currently is.
    let currentCpuOverlay = null;

    function hasProgress() {
        return history.length > 0;
    }

    function snapshot() {
        return JSON.parse(JSON.stringify({ remaining, turnHistory, currentLegNumber, dartsThrown, finishes, sets, legs, activeIndex, matchComplete }));
    }

    function getState() {
        const state = snapshot();
        state.lastToast = lastToast;
        state.lastLegWin = lastLegWin;
        state.cpuTurnLive = cpuTurnLive;
        return state;
    }

    // The standard 3-dart average formula (total points scored / total darts
    // thrown * 3), not simply points/turns - the two only coincide because
    // every non-finishing turn here uses exactly 3 darts (you can't choose to
    // stop early without either busting or checking out).
    function average(player) {
        const darts = dartsThrown[player];
        if (!darts) return '0.0';
        const total = turnHistory[player].reduce((sum, t) => sum + t.score, 0);
        return (total / darts * 3).toFixed(1);
    }

    // Same 3-dart average formula as average(), but scoped to a single leg
    // (the current one by default) instead of the whole match.
    function legAverage(player, legNumber = currentLegNumber) {
        const legTurns = turnHistory[player].filter(t => t.leg === legNumber);
        const darts = legTurns.reduce((sum, t) => sum + t.darts, 0);
        if (!darts) return '0.0';
        const total = legTurns.reduce((sum, t) => sum + t.score, 0);
        return (total / darts * 3).toFixed(1);
    }

    // Every leg number any player has thrown a turn in, in play order -
    // a union across players rather than just one player's turnHistory,
    // since a leg where a player hasn't thrown yet (they go second) would
    // otherwise be missed.
    function playedLegNumbers() {
        const legNumbers = new Set();
        currentPlayers.forEach(player => {
            (turnHistory[player] || []).forEach(t => legNumbers.add(t.leg));
        });
        return Array.from(legNumbers).sort((a, b) => a - b);
    }

    // Everything the stats modal shows for one player, computed fresh from
    // turnHistory/dartsThrown/finishes rather than kept as running state -
    // there's no hot path calling this (only opening/switching tabs in the
    // modal), so recomputing is simpler than maintaining yet more counters.
    function statsFor(player) {
        const turns = turnHistory[player] || [];
        const darts = dartsThrown[player] || 0;
        const total = turns.reduce((sum, t) => sum + t.score, 0);

        const buckets = { '180': 0, '160-179': 0, '140-159': 0, '120-139': 0, '100-119': 0, '80-99': 0, '60-79': 0, '0-59': 0 };
        turns.forEach(t => {
            const s = t.score;
            if (s === 180) buckets['180'] += 1;
            else if (s >= 160) buckets['160-179'] += 1;
            else if (s >= 140) buckets['140-159'] += 1;
            else if (s >= 120) buckets['120-139'] += 1;
            else if (s >= 100) buckets['100-119'] += 1;
            else if (s >= 80) buckets['80-99'] += 1;
            else if (s >= 60) buckets['60-79'] += 1;
            else buckets['0-59'] += 1;
        });

        const playerFinishes = finishes[player] || [];
        const highestFinish = playerFinishes.length ? Math.max(...playerFinishes.map(f => f.score)) : null;
        // "Best leg" only makes sense among legs this player actually won -
        // a leg they lost has no finish, so no darts-to-checkout figure to
        // compare against the others.
        const bestLeg = playerFinishes.length ? Math.min(...playerFinishes.map(f => f.darts)) : null;
        const playedLegs = new Set(turns.map(t => t.leg)).size;

        return {
            threeDartAvg: darts ? (total / darts * 3).toFixed(1) : '0.0',
            oneDartAvg: darts ? (total / darts).toFixed(1) : '0.0',
            buckets,
            highestFinish,
            bestLeg,
            dartsThrown: darts,
            playedLegs
        };
    }

    function keysHTML() {
        return `
            <button class="keypad-btn" data-key="1">1</button>
            <button class="keypad-btn" data-key="2">2</button>
            <button class="keypad-btn" data-key="3">3</button>
            <button class="keypad-btn" data-key="4">4</button>
            <button class="keypad-btn" data-key="5">5</button>
            <button class="keypad-btn" data-key="6">6</button>
            <button class="keypad-btn" data-key="7">7</button>
            <button class="keypad-btn" data-key="8">8</button>
            <button class="keypad-btn" data-key="9">9</button>
            <button class="keypad-btn utility" data-key="clear" title="Clear" aria-label="Clear"><i class="fa-solid fa-trash-can"></i></button>
            <button class="keypad-btn" data-key="0">0</button>
            <button class="keypad-btn utility" data-key="back" title="Backspace" aria-label="Backspace"><i class="fa-solid fa-delete-left"></i></button>
        `;
    }

    function tierBadgeHTML(player) {
        const profile = cpuPlayers[player];
        const tier = profile && DIFFICULTY_TIERS.find(t => t.throwProfile.accuracyStdDev === profile.accuracyStdDev);
        return tier ? `<span class="tier-badge tier-badge-${tier.id} x01-tier-badge">${tier.label}</span>` : '';
    }

    function boardHTML(players, observer) {
        const cards = players.map((player, i) => `
            <div class="x01-player" id="x01-player-${i}">
                <div class="x01-player-header">
                    <div class="x01-player-id">
                        <span class="x01-turn-dot" id="x01-badge-${i}" style="visibility:hidden"></span>
                        <span class="x01-player-name-col">
                            <span class="x01-player-name">${player}</span>
                            ${tierBadgeHTML(player)}
                        </span>
                    </div>
                    <div class="x01-player-averages">
                        <div class="x01-stat-chip">
                            <span class="x01-stat-label">Avg</span>
                            <span class="x01-stat-value" id="x01-avg-${i}"></span>
                        </div>
                        <div class="x01-stat-chip">
                            <span class="x01-stat-label">Leg</span>
                            <span class="x01-stat-value" id="x01-leg-avg-${i}"></span>
                        </div>
                    </div>
                </div>
                <div class="x01-player-body">
                    <div class="x01-player-remaining" id="x01-remaining-${i}">${startingScore}</div>
                    <div class="x01-player-checkout" id="x01-checkout-${i}"></div>
                </div>
            </div>
        `).join('');

        const quickPicksHTML = QUICK_SCORES.map(s => `<button class="x01-quick-btn" data-score="${s}">${s}</button>`).join('');

        // A single-set match ("best of 1") makes "sets" a meaningless label -
        // the match is really just a race to legs, so the header names that
        // instead.
        const titleSuffix = bestOfSets === 1 ? `Best of ${bestOfLegs} Legs` : `Best of ${bestOfSets} Sets`;

        const statsBtnHTML = `
            <button type="button" class="x01-stats-btn" id="x01StatsBtn" title="Statistics" aria-label="Statistics">
                <i class="fa-solid fa-chart-simple"></i>
            </button>
        `;

        // Observer mount (a display-only client, e.g. a wall-mounted tablet by
        // the board): no numpad/quick-picks/undo, no "how many darts?" modal -
        // those only exist to drive input this client never sends. Uses the
        // shared wall-display layout (.obs-* in styles.css) rather than the
        // player screen's cards; renderAll() fills in the same element ids.
        if (observer) {
            const showSets = bestOfSets > 1;
            const columns = players.length <= 3 ? players.length : Math.ceil(players.length / 2);
            const observerCards = players.map((player, i) => `
                <div class="obs-col" id="x01-player-${i}">
                    <div class="obs-who">
                        <span class="obs-dot" id="x01-badge-${i}" style="visibility:hidden"></span>
                        <span class="obs-name">${escapeHTML(player)}</span>
                    </div>
                    <div class="obs-main">
                        <div class="obs-score" id="x01-remaining-${i}">${startingScore}</div>
                        <div class="obs-pill" id="x01-checkout-${i}"></div>
                    </div>
                    <div class="obs-stats">
                        <div class="obs-stat"><span class="obs-stat-label">Avg</span><span class="obs-stat-value" id="x01-avg-${i}"></span></div>
                        <div class="obs-stat"><span class="obs-stat-label">Leg</span><span class="obs-stat-value" id="x01-leg-avg-${i}"></span></div>
                        ${showSets ? `<div class="obs-stat"><span class="obs-stat-label">Sets</span><span class="obs-stat-value" id="x01-sets-${i}"></span></div>` : ''}
                        <div class="obs-stat"><span class="obs-stat-label">Legs</span><span class="obs-stat-value" id="x01-legs-${i}"></span></div>
                    </div>
                </div>
            `).join('');
            return `
                <div class="obs-board" style="--obs-cols:${columns};--obs-n:${players.length}">
                    <div class="obs-head">
                        <span>${startingScore} &middot; ${titleSuffix}</span>
                        <span class="obs-sub" id="x01SetLegRow"></span>
                        ${statsBtnHTML}
                    </div>
                    <div class="obs-cols${players.length > 3 ? ' obs-dense' : ''}${players.length > 4 ? ' obs-dense3' : ''}">${observerCards}</div>
                </div>
                <div class="x01-toast" id="x01Toast"></div>
                ${legWonModalHTML()}
                ${statsModalHTML()}
            `;
        }

        return `
            <div class="x01-board">
                <div class="x01-board-header">
                    <span class="x01-board-title">${startingScore} &middot; ${titleSuffix}</span>
                    ${statsBtnHTML}
                </div>
                <div class="x01-set-leg-row" id="x01SetLegRow"></div>
                <div class="x01-scoreboard">${cards}</div>
                <div class="x01-turn-panel">
                    <div class="keypad-display" id="x01Display"></div>
                    <div class="x01-quick-picks" id="x01QuickPicks">${quickPicksHTML}</div>
                    <div class="keypad-grid" id="x01Keys">${keysHTML()}</div>
                    <div class="x01-action-row">
                        <button class="btn-secondary" id="x01UndoBtn" disabled><i class="fa-solid fa-rotate-left"></i> Undo</button>
                        <button class="btn" id="x01SubmitBtn">Confirm Turn</button>
                    </div>
                </div>
            </div>
            <div class="x01-toast" id="x01Toast"></div>
            <div class="modal-backdrop x01-darts-backdrop" id="x01DartsBackdrop"></div>
            <div class="modal x01-darts-modal" id="x01DartsModal">
                <h3 class="modal-title">How many darts used?</h3>
                <div class="toggle-group" id="x01DartsToggle" data-value="">
                    <button type="button" class="toggle-btn" data-value="1">1</button>
                    <button type="button" class="toggle-btn" data-value="2">2</button>
                    <button type="button" class="toggle-btn" data-value="3">3</button>
                </div>
                <button class="btn x01-darts-submit-btn" id="x01DartsSubmitBtn" disabled title="Confirm" aria-label="Confirm">
                    <i class="fa-solid fa-check"></i>
                </button>
            </div>
            ${legWonModalHTML()}
            ${statsModalHTML()}
        `;
    }

    function legWonModalHTML() {
        return `
            <div class="modal-backdrop" id="x01LegWonBackdrop"></div>
            <div class="modal x01-legwon-modal" id="x01LegWonModal">
                <div class="x01-legwon-title" id="x01LegWonTitle"></div>
                <div class="x01-legwon-avg">
                    <div class="x01-legwon-avg-value" id="x01LegWonAvg"></div>
                    <div class="x01-legwon-avg-label">Leg average</div>
                </div>
                <div class="x01-stats-grid" id="x01LegWonTable"></div>
                <div class="x01-legwon-next" id="x01LegWonNext"></div>
                <button class="btn x01-legwon-btn" id="x01LegWonBtn">Game on!</button>
            </div>
        `;
    }

    function statsModalHTML() {
        return `
            <div class="modal-backdrop" id="x01StatsBackdrop"></div>
            <div class="modal x01-stats-modal" id="x01StatsModal">
                <div class="modal-header">
                    <h3 class="modal-title">Statistics</h3>
                    <button class="modal-close" id="x01StatsCloseBtn" title="Close" aria-label="Close">&times;</button>
                </div>
                <div class="modal-scroll-body">
                    <div class="x01-stats-grid" id="x01StatsBody"></div>
                </div>
            </div>
        `;
    }

    function escapeHTML(text) {
        return String(text).replace(/[&<>"']/g, ch => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]));
    }

    // The same header/row cells as the statistics modal's table (see
    // statsRowsHTML), showing where the match stands right now - sets are
    // left out of a single-set match, where "set" isn't a meaningful label.
    function legWonTableHTML() {
        const header = currentPlayers.map((player, i) =>
            `<div class="x01-stats-player-name" style="color:${playerColor(i)}">${escapeHTML(player)}</div>`
        ).join('');
        const row = (label, values) => `
            <div class="x01-stats-row-label">${label}</div>
            ${values.map(v => `<div class="x01-stats-row-value">${v}</div>`).join('')}
        `;
        return `
            <div class="x01-stats-row-label"></div>
            ${header}
            ${bestOfSets > 1 ? row('Sets', currentPlayers.map(p => sets[p])) : ''}
            ${row('Legs', currentPlayers.map(p => legs[p]))}
        `;
    }

    function legWonModalOpen() {
        const modal = document.getElementById('x01LegWonModal');
        return !!modal && isModalOpen('x01LegWonModal');
    }

    function hideLegWonModal() {
        const wasOpen = legWonModalOpen();
        clearTimeout(legWonTimer);
        if (wasOpen) hidePlainModal('x01LegWonModal', 'x01LegWonBackdrop');
        // A CPU player up next for the new leg has to wait for this to
        // actually close first (whether by the "Game on!" button or its own
        // timeout) - maybeRunCpuTurn() itself refuses to start while the
        // modal's still open (see its own guard), so nothing else would ever
        // give it a second chance to run once it does close.
        if (wasOpen) maybeRunCpuTurn();
    }

    // Shown on every client (observers too - they just get no "Game on!"
    // button, the modal closes by itself once the next leg's first turn
    // arrives). Only for a leg or set win; a match win has the victory modal.
    async function openLegWonModal(win) {
        if (simulating || !document.getElementById('x01LegWonModal')) return;
        const winnerIndex = currentPlayers.indexOf(win.player);
        const nextIndex = currentPlayers.indexOf(win.next);
        document.getElementById('x01LegWonTitle').innerHTML =
            `<span style="color:${playerColor(winnerIndex)}">${escapeHTML(win.player)}</span> wins the ${win.kind}`;
        const avgEl = document.getElementById('x01LegWonAvg');
        avgEl.textContent = win.average;
        avgEl.style.color = playerColor(winnerIndex);
        const table = document.getElementById('x01LegWonTable');
        table.style.setProperty('--x01-stats-players', currentPlayers.length);
        table.innerHTML = legWonTableHTML();
        document.getElementById('x01LegWonNext').innerHTML =
            `<span class="x01-legwon-dot" style="background:${playerColor(nextIndex)}"></span>` +
            `<span><span class="x01-legwon-next-name" style="color:${playerColor(nextIndex)}">${escapeHTML(win.next)}</span> to throw first</span>`;
        const btn = document.getElementById('x01LegWonBtn');
        btn.style.display = observerMode ? 'none' : '';
        // Restart the fill animation from empty every time the modal opens.
        btn.style.setProperty('--x01-legwon-duration', `${LEG_WON_AUTOCLOSE_MS}ms`);
        btn.classList.remove('counting');

        // Only one modal can be open at a time (see core/modal.js) - an
        // observer browsing the statistics when a win arrives has to yield.
        if (document.getElementById('x01StatsModal') && isModalOpen('x01StatsModal')) {
            await hidePlainModal('x01StatsModal', 'x01StatsBackdrop');
        }
        if (!document.getElementById('x01LegWonModal') || legWonModalOpen()) return;
        showPlainModal('x01LegWonModal', 'x01LegWonBackdrop');
        void btn.offsetWidth; // flush the reset above so the animation restarts
        btn.classList.add('counting');
        clearTimeout(legWonTimer);
        legWonTimer = setTimeout(hideLegWonModal, LEG_WON_AUTOCLOSE_MS);
    }

    function announceLegWin(kind, player, average) {
        legWinSeq += 1;
        lastLegWin = { seq: legWinSeq, kind, player, average, next: currentPlayers[activeIndex] };
        lastAppliedLegWinSeq = legWinSeq;
        openLegWonModal(lastLegWin);
    }

    function showToast(msg, kind, withUndo, duration) {
        toastSeq += 1;
        lastToast = { msg, kind, seq: toastSeq, duration };
        lastAppliedToastSeq = toastSeq; // showing it ourselves right now - nothing for setState() to replay later
        renderToast(msg, kind, withUndo, duration);
    }

    // The actual DOM update, split out from showToast() so a toast that
    // arrived via setState() (someone else's action, replayed for this
    // client) can be rendered the same way without re-triggering toastSeq/
    // lastToast bookkeeping meant for this client's own actions - a synced
    // toast is display-only here regardless (never `withUndo`), since the
    // undo button's target turn belongs to whoever actually took it.
    function renderToast(msg, kind, withUndo, duration) {
        const el = document.getElementById('x01Toast');
        if (!el) return;
        el.innerHTML = msg + (withUndo ? ' <button class="x01-toast-undo" id="x01ToastUndoBtn">Undo</button>' : '');
        el.className = 'x01-toast show ' + (kind || '');
        // A toast with nothing to click (no undo button) must never swallow
        // taps meant for whatever's underneath it, however long it lingers -
        // the leg/set/match win toasts especially now stay up long enough to
        // otherwise block the board's own Undo button.
        el.style.pointerEvents = withUndo ? '' : 'none';
        clearTimeout(toastTimer);
        toastTimer = setTimeout(hideToast, duration || 2000);
    }

    function hideToast() {
        const el = document.getElementById('x01Toast');
        if (el) el.classList.remove('show');
        clearTimeout(toastTimer);
    }

    // The "how many darts?" modal deliberately has no backdrop click, no
    // Escape handling, and no close button - it's shown/hidden with plain
    // style.display rather than core/modal.js's showPlainModal (which arms a
    // back-button guard to CLOSE the modal on back-press, exactly what this
    // one must not allow). A finish must be accounted for before play
    // continues.
    function showDartsModal() {
        document.getElementById('x01DartsBackdrop').style.display = 'block';
        document.getElementById('x01DartsModal').style.display = 'block';
    }

    function hideDartsModal() {
        const backdrop = document.getElementById('x01DartsBackdrop');
        const modal = document.getElementById('x01DartsModal');
        if (backdrop) backdrop.style.display = 'none';
        if (modal) modal.style.display = 'none';
    }

    function openDartsModal(checkedOutFrom) {
        const minDarts = x01.minDartsForCheckout(checkedOutFrom);
        const group = document.getElementById('x01DartsToggle');
        // 3 is the only possible dart count once the minimum is 3 (the max
        // any turn can ever use) - preselect it rather than making the
        // player tap the one option left available to them.
        const onlyOption = minDarts === 3 ? '3' : '';
        group.dataset.value = onlyOption;
        group.querySelectorAll('.toggle-btn').forEach(btn => {
            const tooFew = minDarts != null && parseInt(btn.dataset.value, 10) < minDarts;
            btn.disabled = tooFew;
            btn.classList.toggle('active', btn.dataset.value === onlyOption);
        });
        document.getElementById('x01DartsSubmitBtn').disabled = !onlyOption;
        showDartsModal();
    }

    // The three ways a turn can actually resolve, factored out so both a
    // human's path (confirmTurn/resolveDartsForFinish, below) and a CPU's
    // (commitAiTurn, further down) commit through the exact same logic -
    // the AI's own plan already knows dart-for-dart which of these applies
    // (see games/shared/x01-ai.js's simulateAiTurn), rather than needing to
    // re-derive it from a bare total the way confirmTurn does. Each one ends
    // by checking whether the turn it just handed off to is a Computer's -
    // the one place all three (not just the CPU's own commitAiTurn) need to,
    // since a human can just as easily hand off to a CPU as to another
    // human.

    function bustTurn(player, before) {
        showToast(`BUST &mdash; ${player} stays on ${before}`, 'bust');
        turnHistory[player].push({ score: 0, leg: currentLegNumber, darts: 3 });
        dartsThrown[player] += 3;
        activeIndex = (activeIndex + 1) % currentPlayers.length;
        renderAll();
        notifyChange();
        maybeRunCpuTurn();
    }

    function reduceTurn(player, score, { showRecordedToast = false } = {}) {
        remaining[player] = remaining[player] - score;
        turnHistory[player].push({ score, leg: currentLegNumber, darts: 3 });
        dartsThrown[player] += 3;
        if (showRecordedToast) {
            showToast(`${score} recorded for ${player}`, '', true);
        }
        activeIndex = (activeIndex + 1) % currentPlayers.length;
        renderAll();
        notifyChange();
        maybeRunCpuTurn();
    }

    // A genuine checkout - `darts` is how many of this turn's darts it
    // actually took, known exactly for a CPU's own simulated turn, or
    // supplied by a human via openDartsModal/resolveDartsForFinish below.
    function finishTurn(player, score, darts) {
        // Total darts this player threw across the whole leg, not just this
        // finishing turn - every earlier turn in the same leg was a full 3
        // darts (you can't choose to stop early without busting or
        // checking out), so this is exact, not an estimate.
        const dartsInLeg = turnHistory[player]
            .filter(t => t.leg === currentLegNumber)
            .reduce((sum, t) => sum + t.darts, 0) + darts;

        dartsThrown[player] += darts;
        turnHistory[player].push({ score, leg: currentLegNumber, darts });
        finishes[player].push({ leg: currentLegNumber, score, darts: dartsInLeg });
        remaining[player] = 0;

        // The leg just finished, so all of its turns are pushed above already.
        const finishedLegAverage = legAverage(player, currentLegNumber);
        const legAverageHTML = `<span class="x01-toast-avg">(${finishedLegAverage} avg)</span>`;
        // Double duration so the leg average has time to register alongside
        // the win itself, not just flash by with the default toast timing.
        const legWinDuration = 4000;

        // A leg or set win gets the "leg won" modal (announced below, once
        // the next leg's starter is known); only a match win still toasts,
        // right before the victory modal.
        let winKind = 'leg';
        legs[player] += 1;
        if (legs[player] >= legsToWin) {
            sets[player] += 1;
            if (sets[player] >= setsToWin) {
                matchComplete = true;
                showToast(`${player} wins the match! ${legAverageHTML}`, 'win', false, legWinDuration);
            } else {
                winKind = 'set';
            }
        }

        // Only reset for a new set/leg if there's actually a next one to
        // play - resetting on the match-ending finish would erase the very
        // legs/remaining totals the victory screen (and this leg's own
        // display, for an instant) needs to show.
        if (!matchComplete) {
            if (legs[player] >= legsToWin) {
                currentPlayers.forEach(p => { legs[p] = 0; });
            }
            currentPlayers.forEach(p => { remaining[p] = startingScore; });
            currentLegNumber += 1; // every turn pushed from here on belongs to the new leg
            // Round robin: each set opens one seat after the previous set's opener, and legs within a set rotate one seat from there, whoever won the last one.
            const setsPlayed = currentPlayers.reduce((sum, p) => sum + sets[p], 0);
            const legsPlayedInSet = currentPlayers.reduce((sum, p) => sum + legs[p], 0);
            activeIndex = (setsPlayed + legsPlayedInSet) % currentPlayers.length;
            announceLegWin(winKind, player, finishedLegAverage);
        }

        renderAll();
        notifyChange();
        if (!matchComplete) maybeRunCpuTurn();
    }

    // Finalizes the turn that was put on hold by openDartsModal() - the mirror
    // of confirmTurn()'s finish branch, but now with a real dart count to
    // charge against the average instead of assuming 3.
    function resolveDartsForFinish(darts) {
        // A finish always shows its own leg/set/match announcement below,
        // regardless of whether the triggering tap asked for a "recorded"
        // toast.
        const { player, score } = pendingFinish;
        pendingFinish = null;
        hideDartsModal();
        finishTurn(player, score, darts);
    }

    function renderAll() {
        currentPlayers.forEach((player, i) => {
            const isTurn = i === activeIndex && !matchComplete;

            const remEl = document.getElementById(`x01-remaining-${i}`);
            if (remEl) remEl.textContent = remaining[player];

            const card = document.getElementById(`x01-player-${i}`);
            if (card) card.classList.toggle('active', isTurn);

            const setsEl = document.getElementById(`x01-sets-${i}`);
            if (setsEl) setsEl.textContent = sets[player];
            const legsEl = document.getElementById(`x01-legs-${i}`);
            if (legsEl) legsEl.textContent = legs[player];

            const badge = document.getElementById(`x01-badge-${i}`);
            if (badge) badge.style.visibility = isTurn ? 'visible' : 'hidden';

            const avg = document.getElementById(`x01-avg-${i}`);
            if (avg) avg.textContent = average(player);

            const legAvgEl = document.getElementById(`x01-leg-avg-${i}`);
            if (legAvgEl) legAvgEl.textContent = legAverage(player);

            const checkout = x01.checkoutFor(remaining[player]);
            const coEl = document.getElementById(`x01-checkout-${i}`);
            if (coEl) coEl.textContent = checkout || '';
        });

        const undoBtn = document.getElementById('x01UndoBtn');
        if (undoBtn) undoBtn.disabled = history.length === 0;

        const setLegRow = document.getElementById('x01SetLegRow');
        if (setLegRow && observerMode) {
            // Each player's own sets/legs sit in their column, so the header only says which set and leg is being played.
            const legNo = currentPlayers.reduce((sum, p) => sum + legs[p], 0) + 1;
            const setNo = currentPlayers.reduce((sum, p) => sum + sets[p], 0) + 1;
            setLegRow.innerHTML = bestOfSets === 1 ? `Leg <b>${legNo}</b>` : `Set <b>${setNo}</b> &middot; Leg <b>${legNo}</b>`;
        } else if (setLegRow) {
            const legsStr = currentPlayers.map(p => legs[p]).join('–');
            if (bestOfSets === 1) {
                // Best-of-1 sets means "sets" aren't a real match dimension -
                // showing "SET 0-0" forever would just be noise.
                setLegRow.innerHTML = `LEG <b>${legsStr}</b>`;
            } else {
                const setsStr = currentPlayers.map(p => sets[p]).join('–');
                setLegRow.innerHTML = `SET <b>${setsStr}</b> &middot; LEG <b>${legsStr}</b>`;
            }
        }
    }

    // All players' stats side by side, one column each - a grid rather than
    // a table so each row's label can sit in its own wide first column
    // without a <table>'s layout quirks (colspan-ing section headers across
    // a dynamic player count, etc.).
    // Same 5-color rotation the scoreboard cards use (see styles.css's
    // shared --accent block for .x01-player etc.) - applied inline here
    // since this grid's value cells aren't one-per-row elements the same
    // nth-of-type trick could target.
    function playerColor(i) {
        return `var(--player-${(i % 5) + 1})`;
    }

    // The grid's inner cells (header row + every stat row), shared by the
    // in-game stats modal and the victory modal's own copy of this table -
    // both just need this dropped into a `.x01-stats-grid` container, they
    // just get there differently (innerHTML into a persistent element here,
    // a returned string there).
    function statsRowsHTML() {
        const allStats = currentPlayers.map(statsFor);

        const header = currentPlayers.map((player, i) =>
            `<div class="x01-stats-player-name" style="color:${playerColor(i)}">${player}</div>`
        ).join('');
        const section = (label) => `<h4 class="x01-stats-section-title">${label}</h4>`;
        const row = (label, getValue) => `
            <div class="x01-stats-row-label">${label}</div>
            ${allStats.map(s => `<div class="x01-stats-row-value">${getValue(s)}</div>`).join('')}
        `;
        const legAvgRows = playedLegNumbers().map(legNumber => `
            <div class="x01-stats-row-label">Leg ${legNumber + 1}</div>
            ${currentPlayers.map(player => `<div class="x01-stats-row-value">${legAverage(player, legNumber)}</div>`).join('')}
        `).join('');

        return `
            <div class="x01-stats-row-label"></div>
            ${header}

            ${section('Game Averages')}
            ${row('3 Dart', s => s.threeDartAvg)}
            ${row('1 Dart', s => s.oneDartAvg)}

            ${section('Leg Averages')}
            ${legAvgRows}

            ${section('Amount')}
            ${row("180's", s => s.buckets['180'])}
            ${row('160 - 179', s => s.buckets['160-179'])}
            ${row('140 - 159', s => s.buckets['140-159'])}
            ${row('120 - 139', s => s.buckets['120-139'])}
            ${row('100 - 119', s => s.buckets['100-119'])}
            ${row('80 - 99', s => s.buckets['80-99'])}
            ${row('60 - 79', s => s.buckets['60-79'])}
            ${row('0 - 59', s => s.buckets['0-59'])}

            ${section('Finish')}
            ${row('Highest', s => s.highestFinish ?? 'N.A.')}

            ${section('Legs')}
            ${row('Darts Thrown', s => s.dartsThrown)}
            ${row('Best Leg', s => s.bestLeg ?? 'N.A.')}
            ${row('Played Legs', s => s.playedLegs)}
        `;
    }

    function renderStatsBody() {
        const body = document.getElementById('x01StatsBody');
        if (!body) return;
        body.style.setProperty('--x01-stats-players', currentPlayers.length);
        body.innerHTML = statsRowsHTML();
    }

    function openStatsModal() {
        renderStatsBody();
        showPlainModal('x01StatsModal', 'x01StatsBackdrop');
    }

    // A standalone, self-contained version of the same table (own grid
    // wrapper + player-count custom property) for app.js to drop into the
    // victory modal, which has no persistent element of its own to render
    // into the way the in-game stats modal's #x01StatsBody does.
    function matchStatsHTML() {
        return `<div class="x01-stats-grid" style="--x01-stats-players:${currentPlayers.length}">${statsRowsHTML()}</div>`;
    }

    function notifyChange({ suppressComplete = false } = {}) {
        if (!onChangeCallback) return;
        const scores = {};
        const standingLabels = {};
        currentPlayers.forEach(player => {
            // Best-of-1 sets means sets aren't a real match dimension (see
            // renderAll's SET/LEG row) - legs won is the meaningful score
            // then instead.
            scores[player] = bestOfSets === 1 ? legs[player] : sets[player];
            standingLabels[player] = `avg ${average(player)}`;
        });
        // The game's own name/id is always "501" regardless of what starting
        // score this particular match actually used - history/stats should
        // show the real one (e.g. "301"), not the fixed definition name.
        onChangeCallback({
            scores,
            complete: !suppressComplete && matchComplete,
            standingLabels,
            gameTypeLabel: String(startingScore)
        });
    }

    function confirmTurn(score, { showRecordedToast = false } = {}) {
        if (matchComplete || pendingFinish) return;

        const player = currentPlayers[activeIndex];
        const before = remaining[player];
        const after = before - score;
        // A turn that would exactly zero out a remaining with no valid
        // checkout at all (e.g. 168 - no 3-dart combo can end on a double
        // from there) can't be a real finish, whichever entry path it came
        // through (the numpad already blocks this before it gets here, but
        // quick-picks have no equivalent staging step to block in) - treat
        // it the same as any other bust instead of awarding an impossible win.
        const impossibleFinish = after === 0 && !x01.checkoutFor(before);
        history.push(snapshot());

        if (x01.isBust(before, score) || impossibleFinish) {
            bustTurn(player, before);
            return;
        }

        if (after === 0) {
            // A checkout - hold this turn open until the player says how many
            // darts it took (openDartsModal blocks everything else until
            // resolveDartsForFinish() commits it).
            pendingFinish = { player, score, showRecordedToast };
            openDartsModal(before);
            return;
        }

        reduceTurn(player, score, { showRecordedToast });
    }

    // Reflects whether the digits currently on the readout are even a
    // legal 3-dart total (some, like 169, aren't - no combination of 3 darts
    // can ever add up to them) - the readout turns red and Confirm Turn
    // disables itself rather than silently clamping or accepting the value.
    // A typed total is invalid either because no 3-dart combination can ever
    // add up to it at all (169, etc.), or because it exactly matches the
    // active player's remaining (i.e. is a checkout attempt) but that
    // remaining has no valid checkout of any kind (168, etc. - no combo ends
    // on a double from there).
    function isEnteredScoreInvalid(value) {
        if (!x01.isValidTurnScore(value)) return true;
        const before = remaining[currentPlayers[activeIndex]];
        return value === before && !x01.checkoutFor(before);
    }

    function updateNumpadValidity() {
        const display = document.getElementById('x01Display');
        const raw = display.textContent;
        const invalid = raw !== '' && isEnteredScoreInvalid(parseInt(raw, 10));
        display.classList.toggle('invalid', invalid);
        document.getElementById('x01SubmitBtn').disabled = raw === '' || invalid;
    }

    function submitNumpadTurn() {
        const display = document.getElementById('x01Display');
        const raw = display.textContent;
        const score = parseInt(raw, 10);
        if (raw === '' || isEnteredScoreInvalid(score)) return;
        confirmTurn(score);
        digitClear(display);
        resetKeypadEntryState();
        updateNumpadValidity();
    }

    function undoLastTurn() {
        if (pendingFinish) return; // the darts modal blocks reaching this anyway

        // The Undo button is only ever something a human presses, meaning
        // for "undo my last turn" - but a CPU plays its own turn again
        // automatically the moment undoing leaves it up next (see
        // maybeRunCpuTurn() below), so stopping at the very first popped
        // turn would often just undo-and-immediately-redo the same trailing
        // CPU turn, making a human's actual last turn practically
        // un-undoable behind it. Pops one turn at a time instead, skipping
        // backward through any CPU turns, until the turn actually undone
        // belongs to a human (or history runs out first).
        let undoneAny = false;
        while (true) {
            const snap = history.pop();
            if (!snap) break;
            undoneAny = true;
            const undonePlayer = currentPlayers[(activeIndex - 1 + currentPlayers.length) % currentPlayers.length];
            remaining = snap.remaining;
            turnHistory = snap.turnHistory;
            currentLegNumber = snap.currentLegNumber;
            dartsThrown = snap.dartsThrown;
            finishes = snap.finishes;
            sets = snap.sets;
            legs = snap.legs;
            activeIndex = snap.activeIndex;
            matchComplete = snap.matchComplete;
            if (!cpuPlayers[undonePlayer]) break; // a human's own turn - stop here
        }
        if (!undoneAny) return;

        hideToast();
        hideLegWonModal();
        renderAll();
        // Undo must never be what finishes the match - it's a correction,
        // not a turn. matchComplete is always restored to false here anyway
        // (a turn can't be confirmed once true, so history never holds a
        // snapshot taken after the match already ended), but suppressing
        // completion explicitly keeps that guaranteed rather than incidental.
        notifyChange({ suppressComplete: true });
        // Undoing a CPU's own turn puts activeIndex right back on it - with
        // no human input to ever advance a CPU's turn otherwise, it has to
        // throw again here or the match would just stall on it. Only
        // actually fires when the loop above stopped because history ran
        // out entirely (still on a CPU) rather than because it found a
        // human's turn to stop at.
        maybeRunCpuTurn();
    }

    function handleKeyClick(e) {
        const btn = e.target.closest('[data-key]');
        if (!btn) return;
        const display = document.getElementById('x01Display');
        const key = btn.dataset.key;
        if (key === 'clear') digitClear(display);
        else if (key === 'back') digitBackspace(display);
        else digitInput(display, key);
        updateNumpadValidity();
    }

    function handleQuickPickClick(e) {
        const btn = e.target.closest('[data-score]');
        if (!btn) return;
        confirmTurn(parseInt(btn.dataset.score, 10), { showRecordedToast: true });
    }

    // --- Computer-controlled turns ---
    //
    // A CPU player's whole turn is planned upfront (games/shared/x01-ai.js's
    // simulateAiTurn) - every dart's aim, actual landing point, and the
    // outcome that dart produced - rather than driven dart-by-dart through
    // confirmTurn() the way a human's typed total is. That plan already
    // knows, dart for dart, whether the turn ends in a bust, a real
    // (double/bullseye) checkout, or an ordinary score, so it's committed
    // through whichever of bustTurn/finishTurn/reduceTurn actually applies -
    // unlike confirmTurn(), which only ever gets a bare total and has to
    // infer a checkout from "reached exactly 0" alone (see its own
    // impossibleFinish comment). That inference is deliberately looser than
    // real rules (a typed total can't say which dart got there), but a
    // CPU's simulated darts do know, so reaching 0 on anything but a real
    // double/bullseye is committed as a bust here, never a win.
    function commitAiTurn(player, plan) {
        history.push(snapshot());
        if (plan.outcome === 'bust') {
            bustTurn(player, remaining[player]);
        } else if (plan.outcome === 'checkout') {
            finishTurn(player, plan.totalScore, plan.dartsUsed);
        } else {
            reduceTurn(player, plan.totalScore);
        }
    }

    // The overlay's final readout line for a finished (or just-replayed)
    // simulated turn - shared by the driving client's own animateCpuTurn and
    // an observer's syncObserverCpuOverlay so both ever compute it the one
    // way.
    function cpuTurnOutcomeLabel(plan) {
        if (plan.outcome === 'bust') return 'BUST';
        if (plan.outcome === 'checkout') return `Checkout! ${plan.totalScore}`;
        return String(plan.totalScore);
    }

    // Reveals an already-simulated turn's darts one at a time, at a human
    // pace, on the read-only overlay board - this is purely presentational;
    // the turn's actual outcome was already decided by simulateAiTurn()
    // before the first dart is ever drawn, so nothing here can change it.
    // Returns normally once the whole reveal finished, or early (with
    // currentCpuOverlay already cleared) the moment stopCpuTurn() fires
    // mid-animation - checked after every await, since that's the only
    // point this function's own code runs again once interrupted. The
    // caller (maybeRunCpuTurn) checks cpuTurnStopped itself right after
    // this returns, so an interrupted turn is never committed either way.
    //
    // Broadcasts cpuTurnLive at every step (open, each dart, the outcome
    // line) via notifyChange() - the same mechanism a human's own turn uses
    // to sync its result, just called more than once per turn here - so an
    // observer's setState() can mirror this exact reveal live (see
    // syncObserverCpuOverlay) instead of only hearing about the finished
    // turn once this whole function has already returned.
    async function animateCpuTurn(player, plan) {
        cpuTurnSeq += 1;
        const turnSeq = cpuTurnSeq;
        const overlay = showCpuThrowOverlay(player);
        currentCpuOverlay = overlay;
        cpuTurnLive = { seq: turnSeq, player, plan, revealed: 0, done: false };
        notifyChange({ suppressComplete: true });
        await waitBeforeFirstDart(); // let the empty board register before anything lands on it
        let runningTotal = 0;
        for (let i = 0; i < plan.throws.length; i++) {
            if (cpuTurnStopped) return;
            const thrown = plan.throws[i];
            runningTotal += thrown.result.score;
            await revealCpuDart(overlay, thrown, runningTotal, i + 1);
            cpuTurnLive = { seq: turnSeq, player, plan, revealed: cpuTurnLive.revealed + 1, done: false };
            notifyChange({ suppressComplete: true });
        }
        if (cpuTurnStopped) return;
        const outcomeLabel = cpuTurnOutcomeLabel(plan);
        cpuTurnLive = { seq: turnSeq, player, plan, revealed: plan.throws.length, done: true };
        notifyChange({ suppressComplete: true });
        await finishCpuThrowOverlay(overlay, outcomeLabel); // keeps the board open for one more pause before closing
        currentCpuOverlay = null;
        cpuTurnLive = null; // the commit right after this (see maybeRunCpuTurn) carries this null along with the real score change - together, that's the observer's "close the overlay" signal
    }

    // Checks whether it's now a Computer player's turn and, if so, plays it -
    // called defensively from bustTurn/reduceTurn/finishTurn (every real
    // turn-advancing point, whichever of a human's or a CPU's own commitAiTurn
    // reached it) rather than from just one place, so no path has to
    // remember to trigger it - including commitAiTurn's own call into one
    // of those three, which is exactly what lets a CPU-vs-CPU match keep
    // chaining turns on its own. `simulating` guards against also firing
    // during ?debug=1's own full-match simulation below, which drives every
    // "player" (CPU or not) through confirmTurn() directly and would
    // otherwise double up. `legWonModalOpen()` holds off a CPU player up
    // next for a new leg/set until that announcement has actually been
    // dismissed (by the "Game on!" button or its own auto-close timeout) -
    // see hideLegWonModal()'s own call back into this once it does close.
    async function maybeRunCpuTurn() {
        if (matchComplete || pendingFinish || observerMode || simulating || cpuTurnInProgress || legWonModalOpen() || cpuTurnStopped) return;
        const player = currentPlayers[activeIndex];
        const throwProfile = cpuPlayers[player];
        if (!throwProfile) return;

        // Guards the whole sequence below (the optional pre-delay included),
        // not just the animation - commitAiTurn (below) calls straight into
        // bustTurn/reduceTurn/finishTurn, which call maybeRunCpuTurn() again
        // for whoever's up next. That nested call has to see
        // cpuTurnInProgress already cleared, or a CPU-vs-CPU match would
        // stall after its first turn: the guard would still read true (this
        // call hasn't returned yet) and the next player's turn would never
        // start.
        cpuTurnInProgress = true;

        // Only this CPU's own overlay is ever delayed from appearing, and
        // only when the player who just went was also a CPU - back-to-back
        // CPU turns would otherwise replace one overlay with the next
        // before the real board underneath is ever actually seen. This
        // never delays the commit below, so a human up next always sees
        // their opponent's score the instant that opponent's turn actually
        // ends, with nothing to wait through. history.length === 0 (no turn
        // committed yet this match) means there's no real "previous player"
        // to check - activeIndex - 1 would otherwise wrap around to
        // whoever's simply last in turn order, who hasn't actually played.
        const isFirstTurnOfMatch = history.length === 0;
        const previousPlayer = currentPlayers[(activeIndex - 1 + currentPlayers.length) % currentPlayers.length];
        if (!isFirstTurnOfMatch && cpuPlayers[previousPlayer]) {
            await waitBetweenCpuTurns();
        }
        if (cpuTurnStopped) { cpuTurnInProgress = false; return; }

        const plan = simulateAiTurn({ remaining: remaining[player], throwProfile });
        await animateCpuTurn(player, plan);
        cpuTurnInProgress = false;
        // Never commits a turn that stopCpuTurn() interrupted mid-reveal -
        // the game screen was left while it was still animating, so
        // whatever it would have scored is meant to just be abandoned, not
        // silently applied once this async chain finally gets back here.
        if (cpuTurnStopped) return;
        commitAiTurn(player, plan);
    }

    // Called from outside (app.js, when leaving the game screen - back
    // button, Home, Restart) to stop a CPU's turn in its tracks rather than
    // letting it keep simulating and committing turns in the background
    // after the player has already navigated away from it. Removing
    // currentCpuOverlay here is what makes this immediate - the async chain
    // itself only gets a chance to notice cpuTurnStopped and actually stop
    // itself the next time one of its own awaits resolves.
    function stopCpuTurn() {
        cpuTurnStopped = true;
        cpuTurnLive = null;
        if (currentCpuOverlay) {
            currentCpuOverlay.el.remove();
            currentCpuOverlay = null;
        }
    }

    // --- ?debug=1 match simulation ---
    //
    // Plays a full match - through the exact same confirmTurn()/
    // resolveDartsForFinish() every real turn uses, so it automatically
    // respects the actual best-of-sets/best-of-legs and starting score
    // chosen on the setup screen, busts, and checkout rules - rather than
    // fabricating a final state. `missRate` (0-0.2, rolled once per
    // simulated match) is a bad turn that busts; every other turn either
    // goes for a real checkout straight from the checkout chart, or a
    // plausible common scoring total.
    const SIMULATED_COMMON_SCORES = [180, 140, 100, 85, 81, 60, 45, 41, 26, 40, 22, 9];

    function nearestValidScore(score) {
        let s = Math.max(0, Math.min(180, Math.round(score)));
        while (s > 0 && !x01.isValidTurnScore(s)) s -= 1;
        return s;
    }

    function pickBustingScore(before) {
        const busts = SIMULATED_COMMON_SCORES.filter(s => x01.isBust(before, s));
        if (busts.length) return busts[randomIntFromInterval(0, busts.length - 1)];
        return nearestValidScore(before + randomIntFromInterval(2, 40)); // guaranteed overshoot
    }

    function pickNormalScore(before) {
        const safe = SIMULATED_COMMON_SCORES.filter(s => s < before && before - s !== 1);
        if (safe.length) return safe[randomIntFromInterval(0, safe.length - 1)];
        // A remaining none of the common totals fit safely under - any
        // smaller valid total that doesn't bust or leave exactly 1.
        for (let s = Math.min(before, 60); s > 0; s--) {
            if (before - s !== 1 && x01.isValidTurnScore(s)) return s;
        }
        return 0; // nothing safe left to throw but a scoreless turn
    }

    function pickSimulatedTurnScore(before, missRate) {
        if (Math.random() < missRate) return pickBustingScore(before);
        if (x01.checkoutFor(before)) return before; // go for the real finish
        return pickNormalScore(before);
    }

    function simulateMatch(missRate) {
        const maxTurns = 3000; // real matches finish in a small fraction of this
        let turnCount = 0;
        simulating = true; // no "leg won" modal for every simulated leg
        while (!matchComplete && turnCount < maxTurns) {
            turnCount++;
            const player = currentPlayers[activeIndex];
            confirmTurn(pickSimulatedTurnScore(remaining[player], missRate));
            if (pendingFinish) {
                const minDarts = x01.minDartsForCheckout(pendingFinish.score) || 3;
                resolveDartsForFinish(randomIntFromInterval(minDarts, 3));
            }
        }
        simulating = false;
    }

    function applyState(snapshot) {
        remaining = snapshot.remaining;
        turnHistory = snapshot.turnHistory;
        currentLegNumber = snapshot.currentLegNumber;
        dartsThrown = snapshot.dartsThrown;
        finishes = snapshot.finishes;
        sets = snapshot.sets;
        legs = snapshot.legs;
        activeIndex = snapshot.activeIndex;
        matchComplete = snapshot.matchComplete;
        pendingFinish = null; // an in-flight "how many darts?" prompt is this client's own UI state, never part of a shared snapshot
        hideDartsModal();
        if (snapshot.lastToast && snapshot.lastToast.seq !== lastAppliedToastSeq) {
            lastAppliedToastSeq = snapshot.lastToast.seq;
            renderToast(snapshot.lastToast.msg, snapshot.lastToast.kind, false, snapshot.lastToast.duration);
        } else {
            hideToast();
        }
        // The "leg won" modal is up only while nobody has thrown in the new
        // leg yet - joining/reconnecting mid-leg (or any turn arriving
        // after the win) must not resurrect or keep it.
        const win = snapshot.lastLegWin;
        const awaitingFirstThrow = currentPlayers.every(p => !turnHistory[p].some(t => t.leg === currentLegNumber));
        if (win) {
            legWinSeq = Math.max(legWinSeq, win.seq);
            lastLegWin = win;
        }
        if (win && awaitingFirstThrow && !matchComplete) {
            if (win.seq !== lastAppliedLegWinSeq) {
                lastAppliedLegWinSeq = win.seq;
                openLegWonModal(win);
            }
        } else {
            hideLegWonModal();
        }
        renderAll();
        // Without this, a client that receives a match-ending state over
        // sync (rather than playing the finishing turn itself) would repaint
        // the board but never tell app.js's handleGameChange - which is what
        // arms the victory modal - so it'd just sit on the finished board
        // with no modal at all.
        notifyChange();
    }

    // Mirrors a synced CPU turn's live progress (see cpuTurnLive) onto a
    // non-driving client's own board - never re-simulated here, so it always
    // shows exactly what the driving client saw, not a second,
    // independently random turn. Only ever draws whatever's newly arrived
    // since this client last saw this seq (`observerCpuRevealed`), so a
    // duplicate or reconnect envelope with no new progress redraws nothing.
    function syncObserverCpuOverlay(live) {
        if (!live) {
            if (currentCpuOverlay) {
                currentCpuOverlay.el.remove();
                currentCpuOverlay = null;
            }
            observerCpuSeq = 0;
            observerCpuRevealed = 0;
            return;
        }
        if (live.seq !== observerCpuSeq) {
            // A new turn started - open a fresh overlay even if the previous
            // one's own close (cpuTurnLive going back to null) never arrived
            // as its own envelope, so two overlays are never stacked.
            if (currentCpuOverlay) currentCpuOverlay.el.remove();
            currentCpuOverlay = showCpuThrowOverlay(live.player);
            observerCpuSeq = live.seq;
            observerCpuRevealed = 0;
        }
        let runningTotal = live.plan.throws.slice(0, observerCpuRevealed).reduce((sum, t) => sum + t.result.score, 0);
        while (observerCpuRevealed < live.revealed) {
            const thrown = live.plan.throws[observerCpuRevealed];
            runningTotal += thrown.result.score;
            showAimMarker(currentCpuOverlay, thrown, observerCpuRevealed + 1);
            drawCpuDart(currentCpuOverlay, thrown, runningTotal);
            observerCpuRevealed += 1;
        }
        if (live.done) setCpuThrowOutcome(currentCpuOverlay, cpuTurnOutcomeLabel(live.plan));
    }

    // Every client except the one actually driving a given CPU turn mirrors
    // it live this way - an observer, but also a human player's own device
    // in the same session, since cpuPlayers (and so a CPU's actual turns) is
    // only ever local to whichever device's setup declared it (see
    // cpuPlayers' own comment). Guarded on `cpuTurnInProgress` - this
    // client's own "am I mid-way through animating my own CPU's turn right
    // now" flag (see maybeRunCpuTurn) - rather than observerMode, so an
    // unrelated envelope arriving while this client's own CPU turn is still
    // on screen (from some other player's action elsewhere in the session)
    // can never be mistaken for that turn and stomp its overlay out from
    // under it. Stays false the whole time on any client not currently
    // driving a CPU turn at all, exactly the clients this mirrors for.
    // `cpuTurnLive` going back to null is what closes the mirrored overlay:
    // that always arrives in the very same envelope as the real score
    // change it produced (see animateCpuTurn/commitAiTurn), so the two can
    // never be out of step.
    function setState(snapshot) {
        if (!cpuTurnInProgress) syncObserverCpuOverlay(snapshot.cpuTurnLive);
        applyState(snapshot);
    }

    // `cpuPlayers` ({ [player]: throwProfile }, see core/ai-difficulty.js) -
    // only ever non-empty on whichever client's own local playerConfigs
    // actually declared these as Computer players (app.js builds this from
    // that, never from synced state), which is what keeps a CPU's turns
    // from being independently (re-)simulated on every connected client in
    // a shared session - see the AI implementation plan's multiplayer note.
    function mount(container, { players, gameId, onChange, matchConfig, observer = false, cpuPlayers: cpuPlayersOption = {} }) {
        // A previous mount's modal (still open, e.g. on an observer) has to
        // close while its elements still exist - core/modal.js's back-press
        // handler would otherwise be left pointing at ones this replaces.
        hideLegWonModal();
        currentPlayers = players;
        onChangeCallback = onChange;
        observerMode = observer;
        cpuPlayers = cpuPlayersOption;
        cpuTurnInProgress = false;
        // Defensive, in case a previous mount's CPU turn is still mid-flight
        // for some reason (app.js is expected to have already called
        // stopCpuTurn() when leaving that earlier game) - a fresh mount
        // must never inherit a stray overlay or a stuck stopped flag.
        if (currentCpuOverlay) {
            currentCpuOverlay.el.remove();
            currentCpuOverlay = null;
        }
        cpuTurnStopped = false;

        bestOfSets = (matchConfig && matchConfig.bestOfSets) || configRowDefault(x01, 'bestOfSets');
        bestOfLegs = (matchConfig && matchConfig.bestOfLegs) || configRowDefault(x01, 'bestOfLegs');
        startingScore = (matchConfig && matchConfig.startingScore) || configRowDefault(x01, 'startingScore');
        setsToWin = x01.toWin(bestOfSets);
        legsToWin = x01.toWin(bestOfLegs);

        remaining = {};
        turnHistory = {};
        currentLegNumber = 0;
        dartsThrown = {};
        finishes = {};
        sets = {};
        legs = {};
        players.forEach(player => {
            remaining[player] = startingScore;
            turnHistory[player] = [];
            dartsThrown[player] = 0;
            finishes[player] = [];
            sets[player] = 0;
            legs[player] = 0;
        });
        activeIndex = 0;
        history = [];
        matchComplete = false;
        pendingFinish = null;
        toastSeq = 0;
        lastToast = null;
        lastAppliedToastSeq = 0;
        legWinSeq = 0;
        lastLegWin = null;
        lastAppliedLegWinSeq = 0;
        cpuTurnSeq = 0;
        cpuTurnLive = null;
        observerCpuSeq = 0;
        observerCpuRevealed = 0;

        container.innerHTML = boardHTML(players, observer);
        if (!observer) {
            document.getElementById('x01Keys').addEventListener('click', handleKeyClick);
            document.getElementById('x01QuickPicks').addEventListener('click', handleQuickPickClick);
            document.getElementById('x01SubmitBtn').addEventListener('click', submitNumpadTurn);
            document.getElementById('x01UndoBtn').addEventListener('click', undoLastTurn);
            document.getElementById('x01Toast').addEventListener('click', (e) => {
                if (e.target.id === 'x01ToastUndoBtn') undoLastTurn();
                hideToast();
            });
            document.getElementById('x01DartsToggle').addEventListener('click', (e) => {
                const btn = e.target.closest('.toggle-btn');
                if (!btn || btn.disabled) return;
                document.querySelectorAll('#x01DartsToggle .toggle-btn').forEach(b => b.classList.remove('active'));
                btn.classList.add('active');
                document.getElementById('x01DartsToggle').dataset.value = btn.dataset.value;
                document.getElementById('x01DartsSubmitBtn').disabled = false;
            });
            document.getElementById('x01LegWonBtn').addEventListener('click', hideLegWonModal);
            document.getElementById('x01DartsSubmitBtn').addEventListener('click', () => {
                const value = document.getElementById('x01DartsToggle').dataset.value;
                if (!value) return;
                resolveDartsForFinish(parseInt(value, 10));
            });
            resetKeypadEntryState();
            updateNumpadValidity();
        }
        // Wired for both observer and player mounts - a spectator client
        // finds the stats button just as useful, and it never touches any
        // input-only state.
        document.getElementById('x01StatsBtn').addEventListener('click', openStatsModal);
        document.getElementById('x01StatsCloseBtn').addEventListener('click', () => hidePlainModal('x01StatsModal', 'x01StatsBackdrop'));
        document.getElementById('x01StatsBackdrop').addEventListener('click', () => hidePlainModal('x01StatsModal', 'x01StatsBackdrop'));
        renderAll();

        // ?debug=1 simulates a full match so history/stats views can be
        // exercised without clicking through a real one by hand.
        const simulateRandomData = !observer && new URLSearchParams(window.location.search).get('debug');
        if (simulateRandomData) {
            // The real onChange would otherwise fire (and could schedule
            // app.js's finishGame()) on every simulated turn, not just the
            // final one - suppressed here, then notifyChange() is called
            // once by hand below, exactly like a real final turn would,
            // once the simulated match has actually finished. That's what
            // gets app.js's handleGameChange to save history AND show the
            // victory modal - duplicating that here (as this once did,
            // calling saveGameToHistory directly and never notifying at
            // all) desynced debug mode from a real match's ending.
            onChangeCallback = null;
            simulateMatch(Math.random() * 0.2);
            onChangeCallback = onChange;
            renderAll();
            notifyChange();
        } else {
            maybeRunCpuTurn(); // covers a CPU player going first, before any human turn has triggered it
        }
    }

    return { definition: x01, mount, hasProgress, getState, setState, matchStatsHTML, stopCpuTurn };
}
